import { defineStore } from "pinia";
import { ref } from "vue";

import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { streamTypes } from "shared/constants";
import {
  notifyAdditionOfMentionToBookmarkStream,
  notifyRemovalOfMentionFromBookmarkStream,
} from "shared/helpers/actionNotifications";
import { getSource } from "shared/helpers/media";
import { compositeIdToMention, targetType } from "shared/helpers/mentions";
import {
  isSocialBookmarkStream,
  isSocialStream,
  isStream,
} from "shared/helpers/stream";
import StreamService from "shared/services/api/StreamService";
import features from "shared/services/features";
import { type SimilarMentionsStream } from "shared/services/fetch/fetchSimilarMentions";
import { useStreamsStore } from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";
import { type Mention, type SyndicationKey } from "shared/types/mentions";

interface BookmarkMention {
  bookmark_id: number;
}

interface AddToBookmarkStream {
  autoUpdate?: boolean;
  groupId?: Nullable<number>;
  id?: number;
  label: string;
  mention: Mention;
  notify?: boolean;
  organisationTeamId?: Nullable<number>;
  sourceStream?: Nullable<Stream | SimilarMentionsStream>;
}

interface BulkAddToBookmarkStream extends Omit<
  AddToBookmarkStream,
  "mention" | "sourceStream"
> {
  mentionIds: SyndicationKey[];
  sourceStream?: Nullable<Stream>;
}

interface BulkRemoveFromBookmarkStream {
  autoUpdate?: boolean;
  bookmarkStream: Pick<Stream, "id" | "label">;
  mentionIds: SyndicationKey[];
  notify: boolean;
}

interface FindOrCreateBookmarkStream {
  groupId?: Nullable<number>;
  id?: number;
  label?: string;
  organisationTeamId?: Nullable<number>;
  sourceStream?: Nullable<Stream | SimilarMentionsStream>;
}

interface RemoveFromBookmarkStream {
  autoUpdate?: boolean;
  bookmarkStream: Stream;
  mention: BookmarkMention;
  notify?: boolean;
}

interface SharedWithUser {
  access_level: string;
  can_edit: boolean;
  user_id: number;
}

export interface UpdateBookmarkStreamParams {
  color?: string;
  group_id?: number;
  label?: string;
  message?: string;
  notify_user_by_email?: boolean;
  notify_user_by_phone?: boolean;
  sentiment_mode_enabled?: boolean;
  shared_with?: SharedWithUser[];
  social_bookmarks_stream?: boolean;
}

export interface UpdateBookmarkStream {
  id: number;
  params: UpdateBookmarkStreamParams;
}

const handleErrors = true;

export const useBookmarkStreamsStore = defineStore("bookmarkStreams", () => {
  const bookmarksCounts = ref<Record<number, number>>({});

  function addBookmarkStream(params = {}) {
    const stream = {
      boolean_query: "",
      magazine_content: true,
      online_content: true,
      podcast_content: true,
      print_content: true,
      query_style: "boolean",
      radio_content: true,
      sentiment_mode_enabled: true,
      social_content: true,
      social_impact_threshold: 0,
      tv_content: true,
      type: streamTypes.bookmarkStream,
      ...params,
    };

    track("Created Bookmark Stream");

    return $streemApiV1
      .post("streams", { handleErrors, params: { ...stream } })
      .then((response) => {
        useStreamsStore().pushStream(response.data);

        return response.data;
      });
  }

  async function addToBookmarkStream({
    autoUpdate = true,
    groupId = null,
    id,
    label,
    mention,
    notify = true,
    organisationTeamId = null,
    sourceStream = null,
  }: AddToBookmarkStream) {
    track("Created Bookmark");

    const bookmarkStream = await findOrCreateBookmarkStream({
      groupId,
      id,
      label,
      organisationTeamId,
      sourceStream,
    });

    const sourceStreamId = isStream(sourceStream)
      ? sourceStream.id
      : sourceStream?.stream_id;

    const response = await StreamService.createBookmark(
      bookmarkStream.id,
      {
        sourceStreamId,
        targetId: mention.id,
        targetType: targetType(mention),
      },
      { handleErrors }
    );

    if (notify) {
      notifyAdditionOfMentionToBookmarkStream(bookmarkStream.label);
    }

    if (autoUpdate) {
      getBookmarkStreams();

      bookmarksCounts.value = {
        ...bookmarksCounts.value,
        [bookmarkStream.id]: response.data.count,
      };
    }
  }

  async function bulkAddToBookmarkStream({
    autoUpdate = true,
    groupId,
    id,
    label,
    mentionIds,
    notify = true,
    organisationTeamId = null,
    sourceStream = null,
  }: BulkAddToBookmarkStream) {
    track("Created Bookmark", { count: mentionIds.length });

    const bookmarkStream = await findOrCreateBookmarkStream({
      groupId,
      id,
      label,
      organisationTeamId,
      sourceStream,
    });

    const mentions = mentionIds.map(compositeIdToMention);

    const response = await $streemApiV1.post(
      `streams/${bookmarkStream.id}/bookmarks/bulk_create`,
      {
        handleErrors,
        params: {
          mentions: mentions.map((mention) => ({
            target_id: mention.id,
            target_type: targetType(mention),
          })),
          source_stream: sourceStream?.id,
        },
      }
    );

    if (notify) {
      notifyAdditionOfMentionToBookmarkStream(bookmarkStream.label);
    }

    if (autoUpdate) {
      getBookmarkStreams();

      bookmarksCounts.value = {
        ...bookmarksCounts.value,
        [bookmarkStream.id]: response.data.count,
      };
    }
  }

  async function bulkRemoveFromBookmarkStream({
    autoUpdate = true,
    bookmarkStream,
    mentionIds,
    notify = true,
  }: BulkRemoveFromBookmarkStream) {
    track("Deleted Bookmark", { count: mentionIds.length });

    const mentions = mentionIds.map(compositeIdToMention);

    const response = await $streemApiV1.put(
      `streams/${bookmarkStream.id}/bookmarks/bulk_destroy`,
      {
        handleErrors,
        params: {
          mentions: mentions.map((mention) => ({
            target_id: mention.id,
            target_type: getSource(mention.type).relatedCamelCaseField,
          })),
        },
      }
    );

    if (notify) {
      notifyRemovalOfMentionFromBookmarkStream(bookmarkStream.label);
    }

    if (autoUpdate) {
      getBookmarkStreams();

      bookmarksCounts.value = {
        ...bookmarksCounts.value,
        [bookmarkStream.id]: response.data.count,
      };
    }
  }

  function bookmarksCount(id: number) {
    return bookmarksCounts.value[id] || 0;
  }

  function deleteBookmarkStream(id: number) {
    track("Deleted Bookmark Stream");

    return $streemApiV1.delete(`streams/${id}`, { handleErrors }).then(() => {
      useStreamsStore().filterStream({ id });
    });
  }

  async function fetchBookmarkCounts() {
    const { data } = await $streemApiV1.get("bookmarks/counts");
    bookmarksCounts.value = data;
  }

  function findOrCreateBookmarkStream({
    groupId,
    id,
    label,
    organisationTeamId = null,
    sourceStream = null,
  }: FindOrCreateBookmarkStream) {
    return id
      ? useStreamsStore().bookmarkStreams.find((stream) => stream.id === id)
      : addBookmarkStream({
          group_id: groupId,
          label,
          ...(features.has("has_team_workspaces")
            ? { organisation_team_id: organisationTeamId }
            : {}),
          social_bookmarks_stream: Boolean(
            isSocialStream(sourceStream) || isSocialBookmarkStream(sourceStream)
          ),
        });
  }

  function getBookmarkStreams() {
    const params = {
      types: [streamTypes.bookmarkStream],
    };

    return $streemApiV1
      .get("streams", { handleErrors, params })
      .then((response) => useStreamsStore().replaceStreams(response.data));
  }

  async function removeFromBookmarkStream({
    autoUpdate = true,
    bookmarkStream,
    mention,
    notify = true,
  }: RemoveFromBookmarkStream) {
    track("Deleted Bookmark");

    const response = await $streemApiV1.delete(
      `streams/${bookmarkStream.id}/bookmarks/${mention.bookmark_id}`,
      { handleErrors }
    );

    if (notify) {
      notifyRemovalOfMentionFromBookmarkStream(bookmarkStream.label);
    }

    if (autoUpdate) {
      getBookmarkStreams();

      bookmarksCounts.value = {
        ...bookmarksCounts.value,
        [bookmarkStream.id]: response.data.count,
      };
    }
  }

  function updateBookmarkStream({ id, params }: UpdateBookmarkStream) {
    track("Updated Bookmark Stream", {
      shared: Boolean(params.shared_with?.length),
    });

    return $streemApiV1
      .put(`streams/${id}`, { handleErrors, params })
      .then((response) =>
        useStreamsStore().setStream({ data: response.data, id })
      );
  }

  return {
    addBookmarkStream,

    addToBookmarkStream,
    bookmarksCount,
    bookmarksCounts,
    bulkAddToBookmarkStream,
    bulkRemoveFromBookmarkStream,
    deleteBookmarkStream,
    fetchBookmarkCounts,
    findOrCreateBookmarkStream,
    getBookmarkStreams,
    removeFromBookmarkStream,
    updateBookmarkStream,
  };
});

export default useBookmarkStreamsStore;
